import React, { useState, useCallback, useEffect } from 'react'
import {
  Box,
  Button,
  Typography,
  Paper,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Toolbar,
} from '@mui/material'
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  Event as EventIcon,
} from '@mui/icons-material'
import { Calendar, momentLocalizer } from 'react-big-calendar'
import moment from 'moment'
import 'react-big-calendar/lib/css/react-big-calendar.css'
import 'moment/locale/es'
import { getCalendarioFestivos, saveCalendarioFestivo, saveCalendarioFestivoRango, eliminarFestivo, eliminarFestivoRango } from '../../../services/motosSnopServices'

// Configurar moment en español
moment.locale('es')
const localizer = momentLocalizer(moment)

const CalendarioContainer = ({ onAlert }) => {
  // Estados para el calendario
  const [selectedCountry, setSelectedCountry] = useState('co')
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear())
  const [openEventDialog, setOpenEventDialog] = useState(false)
  const [selectedEvent, setSelectedEvent] = useState(null)
  const [newEventTitle, setNewEventTitle] = useState('')
  const [newEventDescription, setNewEventDescription] = useState('')
  const [newEventStart, setNewEventStart] = useState(() => {
    const today = new Date()
    return new Date(today.getFullYear(), today.getMonth(), today.getDate(), 12, 0, 0)
  })
  const [newEventEnd, setNewEventEnd] = useState(() => {
    const today = new Date()
    return new Date(today.getFullYear(), today.getMonth(), today.getDate(), 12, 0, 0)
  })
  const [isEditMode, setIsEditMode] = useState(false)
  const [events, setEvents] = useState({
    co: [],
    cn: [],
  })
  // Estados para eliminar festivos por rango
  const [openDeleteRangeDialog, setOpenDeleteRangeDialog] = useState(false)
  const [deleteRangeStart, setDeleteRangeStart] = useState(() => {
    const today = new Date()
    return new Date(today.getFullYear(), today.getMonth(), today.getDate(), 12, 0, 0)
  })
  const [deleteRangeEnd, setDeleteRangeEnd] = useState(() => {
    const today = new Date()
    return new Date(today.getFullYear(), today.getMonth(), today.getDate(), 12, 0, 0)
  })

  const countries = [
    { code: 'co', name: 'Colombia', flag: '🇨🇴' },
    { code: 'cn', name: 'China', flag: '🇨🇳' },
  ]

  // Funciones para el calendario
  const handleSelectSlot = useCallback(({ start }) => {
    // Usar los componentes de fecha directamente para evitar problemas de zona horaria
    const normalizedDate = new Date(start.getFullYear(), start.getMonth(), start.getDate(), 12, 0, 0)
    setNewEventStart(normalizedDate)
    setNewEventEnd(normalizedDate)
    setNewEventTitle('')
    setNewEventDescription('')
    setIsEditMode(false)
    setSelectedEvent(null)
    setOpenEventDialog(true)
  }, [])

  const handleSelectEvent = useCallback((event) => {
    setSelectedEvent(event)
    setNewEventTitle(event.title || '')
    setNewEventDescription(event.description || '')
    setNewEventStart(event.start)
    setNewEventEnd(event.end)
    setIsEditMode(true)
    setOpenEventDialog(true)
  }, [])

  // Función para detectar cambios de año en el calendario
  const handleNavigate = useCallback((action, newDate) => {
    if (action === 'PREV' || action === 'NEXT') {
      // Calcular la fecha real después de la navegación
      const targetDate = new Date(newDate)
      
      if (action === 'PREV') {
        // Si vamos al mes anterior, restar un mes
        targetDate.setMonth(targetDate.getMonth() - 1)
      } else if (action === 'NEXT') {
        // Si vamos al mes siguiente, sumar un mes
        targetDate.setMonth(targetDate.getMonth() + 1)
      }
      
      const newYear = targetDate.getFullYear()
      if (newYear !== selectedYear) {
        console.log('Cambiando año de', selectedYear, 'a', newYear, 'acción:', action)
        setSelectedYear(newYear)
      }
    }
  }, [selectedYear])

  // Función para obtener datos del calendario
  const fetchCalendarioData = async () => {
    try {
      // Obtener datos para Colombia y China usando el año seleccionado
      const [responseCO, responseCN] = await Promise.all([
        getCalendarioFestivos('co', selectedYear.toString()),
        getCalendarioFestivos('cn', selectedYear.toString())
      ])
      
      console.log('Response Colombia:', responseCO)
      console.log('Response China:', responseCN)
      
      const eventsByCountry = { co: [], cn: [] }
      
      // Procesar datos de Colombia
      if (responseCO && responseCO.festivos) {
        responseCO.festivos.forEach((festivo) => {
          // Asegurar que la fecha se parsee correctamente sin problemas de zona horaria
          const fechaString = festivo.fechas || festivo.fecha
          const [year, month, day] = fechaString.split('-').map(Number)
          const fecha = new Date(year, month - 1, day, 12, 0, 0) // month - 1 porque JavaScript usa base 0 para meses
          
          console.log('Procesando festivo CO:', festivo.nombre, 'Fecha original:', fechaString, 'Fecha parseada:', fecha)
          
          const event = {
            id: festivo.id, // Usar el ID real del backend
            title: festivo.nombre,
            start: fecha,
            end: fecha,
            description: `${festivo.tipo}`,
            allDay: true
          }
          eventsByCountry.co.push(event)
        })
      }
      
      // Procesar datos de China
      if (responseCN && responseCN.festivos) {
        responseCN.festivos.forEach((festivo) => {
          // Asegurar que la fecha se parsee correctamente sin problemas de zona horaria
          const fechaString = festivo.fechas || festivo.fecha
          const [year, month, day] = fechaString.split('-').map(Number)
          const fecha = new Date(year, month - 1, day, 12, 0, 0) // month - 1 porque JavaScript usa base 0 para meses
          
          console.log('Procesando festivo CN:', festivo.nombre, 'Fecha original:', fechaString, 'Fecha parseada:', fecha)
          
          const event = {
            id: festivo.id, // Usar el ID real del backend
            title: festivo.nombre,
            start: fecha,
            end: fecha,
            description: `${festivo.tipo || 'manual'}`,
            allDay: true
          }
          eventsByCountry.cn.push(event)
        })
      }
      
      setEvents(eventsByCountry)
    } catch (error) {
      console.error('Error al obtener el calendario de festivos:', error)
    }
  }

  const handleAddEvent = async () => {
    if (newEventTitle.trim()) {
      // Validar que la fecha fin no sea menor a la fecha de inicio
      if (newEventEnd < newEventStart) {
        onAlert('La fecha de fin no puede ser menor a la fecha de inicio')
        return
      }

      try {
        // Determinar si es un festivo de un solo día o un rango
        const isDateRange = newEventStart.getTime() !== newEventEnd.getTime()
        
        const festivoData = {
          pais: selectedCountry,
          nombre: newEventTitle.trim(),
          origen: selectedCountry === 'co' ? 'COLOMBIA' : 'CHINA',
          fecha: moment(newEventStart).format('YYYY-MM-DD'),
          tipo: 'manual',
        }

        // Para rangos de fechas, agregar fecha fin
        if (isDateRange) {
          festivoData.fechaFin = moment(newEventEnd).format('YYYY-MM-DD')
        }

        // Si es edición, agregar el id
        if (isEditMode && selectedEvent.id) {
          festivoData.id = selectedEvent.id
        }

        // Usar el endpoint apropiado según si es un rango o no
        const response = isDateRange 
          ? await saveCalendarioFestivoRango(festivoData)
          : await saveCalendarioFestivo(festivoData)
        
        if (response) {
          if (isEditMode) {
            // Para edición, actualizar el evento local
            const updatedEvent = {
              id: selectedEvent.id,
              title: newEventTitle.trim(),
              start: newEventStart,
              end: newEventEnd,
              description: newEventDescription || '',
              allDay: true
            }
            
            setEvents(prev => ({
              ...prev,
              [selectedCountry]: prev[selectedCountry].map(event => 
                event.id === selectedEvent.id ? updatedEvent : event
              )
            }))
            onAlert('Festivo actualizado exitosamente')
          } else {
            // Para nuevos festivos, refrescar la lista completa desde el backend
            // para obtener los IDs correctos
            await fetchCalendarioData()
            onAlert('Festivo agregado exitosamente')
          }
          
          setNewEventTitle('')
          setNewEventDescription('')
          setOpenEventDialog(false)
          setIsEditMode(false)
          setSelectedEvent(null)
        } else {
          onAlert('Error al guardar el festivo')
        }
      } catch (error) {
        console.error('Error al guardar festivo:', error)
        onAlert('Error al guardar el festivo')
      }
    }
  }

  const handleDeleteEvent = async () => {
    if (selectedEvent) {
      try {
        // Llamar al endpoint de eliminación usando el ID del evento
        const response = await eliminarFestivo(selectedEvent.id)
        
        if (response && response.success) {
          // Solo eliminar del estado local si el endpoint fue exitoso
          setEvents(prev => ({
            ...prev,
            [selectedCountry]: prev[selectedCountry].filter(event => event.id !== selectedEvent.id)
          }))
          onAlert(response.message || 'Festivo eliminado exitosamente')
        } else {
          onAlert(response?.message || 'Error al eliminar el festivo')
        }
      } catch (error) {
        console.error('Error eliminando festivo:', error)
        onAlert('Error de conexión al eliminar el festivo')
      } finally {
        setOpenEventDialog(false)
        setSelectedEvent(null)
      }
    }
  }

  const handleDeleteRangeEvents = async () => {
    if (deleteRangeEnd < deleteRangeStart) {
      onAlert('La fecha de fin no puede ser menor a la fecha de inicio')
      return
    }

    try {
      const response = await eliminarFestivoRango(
        selectedCountry,
        moment(deleteRangeStart).format('YYYY-MM-DD'),
        moment(deleteRangeEnd).format('YYYY-MM-DD')
      )

      if (response && response.success) {
        // Refrescar los datos del calendario
        await fetchCalendarioData()
        onAlert(response.message || 'Festivos eliminados exitosamente')
        setOpenDeleteRangeDialog(false)
      } else {
        onAlert(response?.message || 'Error al eliminar los festivos')
      }
    } catch (error) {
      console.error('Error al eliminar festivos por rango:', error)
      onAlert('Error al eliminar los festivos')
    }
  }

  const handleOpenDeleteRangeDialog = () => {
    const today = new Date()
    const normalizedToday = new Date(today.getFullYear(), today.getMonth(), today.getDate(), 12, 0, 0)
    setDeleteRangeStart(normalizedToday)
    setDeleteRangeEnd(normalizedToday)
    setOpenDeleteRangeDialog(true)
  }

  // Estilos personalizados para el calendario (todos los festivos en color Auteco)
  const eventStyleGetter = () => ({
      style: {
        backgroundColor: '#4c75aa',
        borderColor: '#4c75aa',
        color: 'white',
        border: 'none',
        borderRadius: '4px',
        fontSize: '12px',
        fontWeight: 'bold'
      }
          })

  // Llamada al endpoint al montar el componente y cuando cambie el año
  useEffect(() => {
    fetchCalendarioData()
  }, [selectedYear])

  return (
    <Box sx={{ display: 'flex', gap: 3 }}>
      {/* Panel izquierdo - Lista de festivos */}
      <Box sx={{ width: 350, flexShrink: 0 }}>
        {/* Selector de país */}
        <FormControl fullWidth sx={{ mb: 3 }}>
          <InputLabel>Seleccionar País</InputLabel>
          <Select
            value={selectedCountry}
            onChange={(e) => setSelectedCountry(e.target.value)}
            label="Seleccionar País"
          >
            {countries.map((country) => (
              <MenuItem key={country.code} value={country.code}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <span style={{ fontSize: '18px' }}>{country.flag}</span>
                  {country.name}
                </Box>
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {/* Lista de festivos */}
        <Paper elevation={2} sx={{ p: 2, borderRadius: 2 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6" color="#4c75aa" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <EventIcon />
              Festivos de {selectedCountry}
            </Typography>
            <Button
              variant="outlined"
              color="error"
              size="small"
              startIcon={<DeleteIcon />}
              onClick={handleOpenDeleteRangeDialog}
              sx={{ 
                borderColor: '#d32f2f',
                color: '#d32f2f',
                '&:hover': { 
                  backgroundColor: '#ffebee',
                  borderColor: '#d32f2f'
                }
              }}
            >
              Eliminar Festivos
            </Button>
          </Box>
          
          <Box sx={{ maxHeight: 500, overflowY: 'auto' }}>
            {(events[selectedCountry] || []).map((event) => (
              <Box 
                key={event.id} 
                sx={{ 
                  p: 2, 
                  mb: 1, 
                  border: '1px solid #e0e0e0',
                  borderRadius: 1,
                  cursor: 'pointer',
                  '&:hover': { backgroundColor: '#f5f5f5' }
                }}
                onClick={() => handleSelectEvent(event)}
              >
                <Typography variant="subtitle2" fontWeight="bold" color="#4c75aa">
                  {event.title}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  {moment(event.start).isValid() ? moment(event.start).format('DD/MM/YYYY') : 'Fecha inválida'}
                  {event.start.getTime() !== event.end.getTime() && moment(event.end).isValid() && 
                    ` - ${moment(event.end).format('DD/MM/YYYY')}`
                  }
                </Typography>
                <Typography variant="body2">
                  {event.description}
                </Typography>
              </Box>
            ))}
            
            {(!events[selectedCountry] || events[selectedCountry].length === 0) && (
              <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
                No hay festivos registrados para {selectedCountry}
              </Typography>
            )}
          </Box>
        </Paper>
      </Box>

      {/* Panel derecho - Calendario */}
      <Box sx={{ flexGrow: 1 }}>
        {/* Toolbar del calendario */}
        <Toolbar 
          sx={{ 
            backgroundColor: '#f8f9fa', 
            borderRadius: 1, 
            mb: 2,
            justifyContent: 'center',
            minHeight: '48px !important'
          }}
        >
          <Typography variant="h6" color="#4c75aa" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <EventIcon />
            Calendario de Festivos - {selectedCountry}
          </Typography>
        </Toolbar>

        {/* Calendario principal */}
        <Paper elevation={2} sx={{ p: 2, borderRadius: 2 }}>
          <Calendar
            localizer={localizer}
            events={events[selectedCountry] || []}
            startAccessor="start"
            endAccessor="end"
            style={{ height: 600 }}
            onSelectSlot={handleSelectSlot}
            onSelectEvent={handleSelectEvent}
            onNavigate={handleNavigate}
            selectable
            popup
            eventPropGetter={eventStyleGetter}
            views={['month']}
            defaultView="month"
            messages={{
              next: "Siguiente",
              previous: "Anterior",
              today: "Hoy",
              month: "Mes",
              date: "Fecha",
              event: "Festivo",
              noEventsInRange: "No hay festivos en este rango",
              showMore: total => `+ Ver más (${total})`
            }}
            formats={{
              monthHeaderFormat: 'MMMM YYYY',
            }}
            components={{
              toolbar: ({ label, onNavigate, date }) => (
                <Box sx={{ 
                  display: 'flex', 
                  justifyContent: 'space-between', 
                  alignItems: 'center',
                  mb: 2,
                  p: 1,
                  backgroundColor: '#4c75aa',
                  borderRadius: 1,
                  color: 'white'
                }}>
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <Button 
                      variant="outlined" 
                      size="small"
                      onClick={() => {
                        onNavigate('PREV')
                        handleNavigate('PREV', date)
                      }}
                      sx={{ color: 'white', borderColor: 'white' }}
                    >
                      ‹
                    </Button>
                    <Button 
                      variant="outlined" 
                      size="small"
                      onClick={() => {
                        onNavigate('TODAY')
                        handleNavigate('TODAY', date)
                      }}
                      sx={{ color: 'white', borderColor: 'white' }}
                    >
                      Hoy
                    </Button>
                    <Button 
                      variant="outlined" 
                      size="small"
                      onClick={() => {
                        onNavigate('NEXT')
                        handleNavigate('NEXT', date)
                      }}
                      sx={{ color: 'white', borderColor: 'white' }}
                    >
                      ›
                    </Button>
                  </Box>
                  
                  <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                    {label}
                  </Typography>
                  
                  <Box sx={{ width: 120 }} />
                </Box>
              )
            }}
          />
        </Paper>
      </Box>

      {/* Diálogo para agregar/editar festivos */}
      <Dialog open={openEventDialog} onClose={() => setOpenEventDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ backgroundColor: '#4c75aa', color: 'white' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <EventIcon />
            {isEditMode ? 'Editar Festivo' : 'Nuevo Festivo'}
          </Box>
        </DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          <TextField
            autoFocus
            margin="dense"
            label="Nombre del festivo"
            fullWidth
            variant="outlined"
            value={newEventTitle}
            onChange={(e) => setNewEventTitle(e.target.value)}
            sx={{ mb: 2 }}
          />
         
          <Box sx={{ display: 'flex', gap: 2 }}>
            <TextField
              label="Fecha de inicio"
              type="date"
              value={moment(newEventStart).format('YYYY-MM-DD')}
              onChange={(e) => {
                const [year, month, day] = e.target.value.split('-').map(Number)
                const date = new Date(year, month - 1, day, 12, 0, 0)
                setNewEventStart(date)
                // Si la fecha de fin es menor que la nueva fecha de inicio, actualizarla
                if (newEventEnd < date) {
                  setNewEventEnd(date)
                }
                // Si no es modo edición o las fechas eran iguales, actualizar fecha fin
                if (!isEditMode || newEventStart.getTime() === newEventEnd.getTime()) {
                  setNewEventEnd(date)
                }
              }}
              InputLabelProps={{ shrink: true }}
              fullWidth
            />
            {!isEditMode && (
            <TextField
              label="Fecha de fin (opcional)"
              type="date"
              value={moment(newEventEnd).format('YYYY-MM-DD')}
              onChange={(e) => {
                const [year, month, day] = e.target.value.split('-').map(Number)
                const selectedDate = new Date(year, month - 1, day, 12, 0, 0)
                if (selectedDate < newEventStart) {
                  onAlert('La fecha de fin no puede ser menor a la fecha de inicio')
                  return
                }
                setNewEventEnd(selectedDate)
              }}
              InputLabelProps={{ shrink: true }}
              fullWidth
              helperText="Para festivos de varios días"
              inputProps={{ 
                min: moment(newEventStart).format('YYYY-MM-DD')
              }}
            />
            )}
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 2, gap: 1 }}>
          {isEditMode && (
            <Button 
              onClick={handleDeleteEvent}
              variant="outlined"
              color="error"
              startIcon={<DeleteIcon />}
            >
              Eliminar
            </Button>
          )}
          <Box sx={{ flexGrow: 1 }} />
          <Button onClick={() => setOpenEventDialog(false)}>
            Cancelar
          </Button>
          <Button 
            onClick={handleAddEvent} 
            variant="contained"
            startIcon={isEditMode ? <EditIcon /> : <AddIcon />}
            sx={{ backgroundColor: '#4c75aa', '&:hover': { backgroundColor: '#3a5a8a' } }}
            disabled={!newEventTitle.trim()}
          >
            {isEditMode ? 'Actualizar' : 'Agregar'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Diálogo para eliminar festivos por rango */}
      <Dialog open={openDeleteRangeDialog} onClose={() => setOpenDeleteRangeDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ backgroundColor: '#d32f2f', color: 'white' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <DeleteIcon />
            Eliminar Festivos por Rango
          </Box>
        </DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          {/* <Typography variant="body1" sx={{ mb: 3, color: '#d32f2f', fontWeight: 'bold' }}>
            ⚠️ Esta acción eliminará todos los festivos del país {selectedCountry} 
            que estén dentro del rango de fechas seleccionado.
          </Typography> */}
          <br />
          <br />
          <br />

          <Box sx={{ display: 'flex', gap: 2 }}>
            <TextField
              label="Fecha de inicio"
              type="date"
              value={moment(deleteRangeStart).format('YYYY-MM-DD')}
              onChange={(e) => {
                const [year, month, day] = e.target.value.split('-').map(Number)
                const date = new Date(year, month - 1, day, 12, 0, 0)
                setDeleteRangeStart(date)
                // Si la fecha de fin es menor que la nueva fecha de inicio, actualizarla
                if (deleteRangeEnd < date) {
                  setDeleteRangeEnd(date)
                }
              }}
              InputLabelProps={{ shrink: true }}
              fullWidth
            />
            <TextField
              label="Fecha de fin"
              type="date"
              value={moment(deleteRangeEnd).format('YYYY-MM-DD')}
              onChange={(e) => {
                const [year, month, day] = e.target.value.split('-').map(Number)
                const selectedDate = new Date(year, month - 1, day, 12, 0, 0)
                if (selectedDate < deleteRangeStart) {
                  onAlert('La fecha de fin no puede ser menor a la fecha de inicio')
                  return
                }
                setDeleteRangeEnd(selectedDate)
              }}
              InputLabelProps={{ shrink: true }}
              fullWidth
              inputProps={{ 
                min: moment(deleteRangeStart).format('YYYY-MM-DD')
              }}
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 2, gap: 1 }}>
          <Button onClick={() => setOpenDeleteRangeDialog(false)}>
            Cancelar
          </Button>
          <Button 
            onClick={handleDeleteRangeEvents} 
            variant="contained"
            color="error"
            startIcon={<DeleteIcon />}
          >
            Eliminar Festivos
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}

export default CalendarioContainer 